export interface WalkthroughDurationAssessment {
  usable: boolean;
  measuredSeconds: number | null;
  displaySeconds: number;
  message: string | null;
}

const MIN_USABLE_WALKTHROUGH_SECONDS = 3;
const MAX_USABLE_PLAYBACK_RATE = 2.5;

/**
 * Browsers can partially decode an unsupported phone .MOV and report a
 * one-second duration. Treat that as a playback failure instead of presenting
 * it as a valid looping store tour.
 */
export function assessWalkthroughDuration(
  measuredSeconds: number,
  expectedSeconds: number | null | undefined,
): WalkthroughDurationAssessment {
  const measured = Number.isFinite(measuredSeconds) && measuredSeconds > 0
    ? Math.round(measuredSeconds)
    : null;
  const expected = Number.isFinite(expectedSeconds) && (expectedSeconds ?? 0) > 0
    ? Math.round(expectedSeconds as number)
    : 0;

  if (measured == null) {
    return {
      usable: false,
      measuredSeconds: null,
      displaySeconds: expected,
      message: 'The browser could not read this walk-through video.',
    };
  }

  if (measured < MIN_USABLE_WALKTHROUGH_SECONDS) {
    const expectedMessage = expected >= MIN_USABLE_WALKTHROUGH_SECONDS
      ? ` It should be ${expected} seconds.`
      : '';
    return {
      usable: false,
      measuredSeconds: measured,
      displaySeconds: expected,
      message: `This video decoded as only ${measured} second${measured === 1 ? '' : 's'}.${expectedMessage} Re-upload it as an H.264 MP4.`,
    };
  }

  return {
    usable: true,
    measuredSeconds: measured,
    displaySeconds: measured,
    message: null,
  };
}

/** Detect broken MOV timebases that make a store tour race to the end. */
export function assessWalkthroughPlaybackPace(
  videoDeltaSeconds: number,
  elapsedMilliseconds: number,
): { usable: boolean; playbackRate: number } {
  if (
    !Number.isFinite(videoDeltaSeconds)
    || !Number.isFinite(elapsedMilliseconds)
    || videoDeltaSeconds < 1
    || elapsedMilliseconds < 250
  ) {
    return { usable: true, playbackRate: 1 };
  }
  const playbackRate = videoDeltaSeconds / (elapsedMilliseconds / 1000);
  return {
    usable: playbackRate <= MAX_USABLE_PLAYBACK_RATE,
    playbackRate,
  };
}
